import React, { useEffect, useState } from "react";
import { Grid, GridColumn, GridSelectionChangeEvent, GridFilterChangeEvent, GridSortChangeEvent } from "@progress/kendo-react-grid";
import { process, CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";

import { useInjection } from "@presentation/components/dependencyInjection";
import { LibraryRepository } from "@infrastructure/repositories/LibraryRepository";
import { FileMetadata } from "@domain/schemas/BI";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { Toolbar } from "@progress/kendo-react-buttons";
import { UploadLibraryButton } from "./components/UploadLibraryButton";
import { useShallow } from "zustand/react/shallow";

const DATA_ITEM_KEY = "path";
const SELECTED_FIELD = "selected";

export interface LibraryExplorerGridProps {
  libraries?: FileMetadata[];
  loading?: boolean;
  onReload?: () => void;
}

const LoadingMask: React.FC = () => (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading...</span>
    <div className="k-loading-image"></div>
    <div className="k-loading-color"></div>
  </div>
);

const SizeCell: React.FC<{ dataItem: FileMetadata & { sizeInGiB?: number | null } }> = ({ dataItem }) => <td>{dataItem.sizeInGiB?.toFixed(2) ?? "-"}</td>;

const DateCell: React.FC<{ dataItem: FileMetadata }> = ({ dataItem }) => {
  const raw = (dataItem as any).createdAt as string | null | undefined;
  if (!raw) return <td style={{ color: "#bbb" }}>—</td>;
  const d = new Date(raw);
  const formatted = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return <td title={d.toISOString()}>{formatted}</td>;
};

export const LibraryExplorerGrid: React.FC<LibraryExplorerGridProps> = ({ libraries: externalLibraries, loading: externalLoading, onReload: externalOnReload }) => {
  const libraryRepo = useInjection(LibraryRepository);
  const [internalLibraries, setInternalLibraries] = useState<FileMetadata[]>([]);
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<CompositeFilterDescriptor>();
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: "createdAt", dir: "desc" }]);
  const [skip, setSkip] = useState<number>(0);
  const [take, setTake] = useState<number>(20);

  // Use external props if provided, otherwise use internal state
  const libraries = externalLibraries ?? internalLibraries;
  const loading = externalLoading ?? internalLoading;
  const [selection, setSelection] = useState<any>({});

  const { setJobLibraryFiles, jobLibraryFiles } = useJobSetupStore(
    useShallow((state) => ({
      setJobLibraryFiles: state.setJobLibraryFiles,
      jobLibraryFiles: state.CreateJobState.libraryFiles || [],
    })),
  );

  // Initial load - only if external libraries are NOT provided
  useEffect(() => {
    if (externalLibraries !== undefined) return;

    const fetchLibraries = async () => {
      setInternalLoading(true);
      try {
        const response = await libraryRepo.getLibraries();
        if (Array.isArray(response)) {
          setInternalLibraries(response);
        } else if ((response as any).data && Array.isArray((response as any).data)) {
          setInternalLibraries((response as any).data);
        } else {
          console.warn("Unexpected response format", response);
          setInternalLibraries([]);
        }
      } catch (error) {
        console.error("Error fetching libraries:", error);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchLibraries();
  }, []);

  const reload = async () => {
    if (externalOnReload) {
      externalOnReload();
      return;
    }

    setInternalLoading(true);
    try {
      const response = await libraryRepo.getLibraries();
      if (Array.isArray(response)) {
        setInternalLibraries(response);
      } else if ((response as any).data && Array.isArray((response as any).data)) {
        setInternalLibraries((response as any).data);
      } else {
        setInternalLibraries([]);
      }
    } catch (error) {
      console.error("Error fetching libraries:", error);
    } finally {
      setInternalLoading(false);
    }
  };

  // Sync store state to local selection on mount/update
  useEffect(() => {
    const newSelection: any = {};
    for (const f of jobLibraryFiles) {
      newSelection[f.path] = true;
    }
    setSelection(newSelection);
  }, [jobLibraryFiles]);

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const path = event.dataItem.path;
    const newSelection = { ...selection };

    // Toggle the clicked item
    if (Object.hasOwn(newSelection, path)) {
      delete newSelection[path];
    } else {
      newSelection[path] = true;
    }

    setSelection(newSelection);

    // Update Store - multiple selections allowed
    const selectedFiles = libraries.filter((f) => newSelection[f.path]);
    setJobLibraryFiles(selectedFiles);
  };

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter);
    setSkip(0);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleDataStateChange = (event: any) => {
    setFilter(event.dataState.filter);
    setSort(event.dataState.sort);
    setSkip(event.dataState.skip);
    setTake(event.dataState.take);
  };

  const data = libraries.map((item) => ({
    ...item,
    [SELECTED_FIELD]: selection[item.path],
    fileName: item.path.split("/").pop() || item.path,
    sizeInGiB: item.sizeInBytes == undefined ? null : item.sizeInBytes / (1024 * 1024 * 1024),
  }));

  const processedData = process(data, { filter, sort, skip, take });

  return (
    <div className="fill-all" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Toolbar className="page-toolbar" style={{ flex: "0 0 auto", height: "50px", borderBottom: "1px solid #e0e0e0" }}>
        <UploadLibraryButton onUploadSuccess={reload} />
      </Toolbar>

      <div style={{ flex: "1 1 auto", minHeight: 0 }}>
        <Grid
          key={libraries.length}
          style={{ height: "100%" }}
          data={processedData.data}
          total={processedData.total}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          selectable={{ enabled: true, mode: "multiple" }}
          onSelectionChange={handleSelectionChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          filterable={true}
          filter={filter}
          onFilterChange={handleFilterChange}
          scrollable="scrollable"
          resizable={true}
          pageable={{ pageSizes: [10, 20, 50, 100, 500], buttonCount: 5 }}
          skip={skip}
          take={take}
          onDataStateChange={handleDataStateChange}
        >
          {loading && <LoadingMask />}
          <GridColumn field={SELECTED_FIELD} title="Selected" width="50px" filterable={false} sortable={false} />
          <GridColumn field="fileName" title="File Name" width="300px" />
          <GridColumn field="path" title="Full Path" />
          <GridColumn field="createdAt" title="Created" width="200px" cell={(properties) => <DateCell dataItem={properties.dataItem} />} />
          <GridColumn field="sizeInGiB" title="Size (GiB)" width="120px" cell={(properties) => <SizeCell dataItem={properties.dataItem} />} filter="numeric" />
        </Grid>
      </div>
    </div>
  );
};
